import React, {
    memo,
    useMemo,
    useState,
    useEffect,
    useRef
} from 'react';

import {
    Box,
    Divider,
    Typography,
    IconButton,
    Tooltip,
    Button
} from '@mui/material';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { useAuthStore } from '../stores/useAuthStore';

import { MOTIVES } from '../utils/tripUtils';
import { m } from '../utils/dateUtils';

const TripOccurrences = ({
    occurrences = [],
    onDelete
}) => {

    const { user } = useAuthStore.getState();

    const occurrencesOrdered =
        useMemo(() => {

            return [...occurrences]
                .sort(
                    (a, b) =>
                        m(b.createdAt).valueOf() -
                        m(a.createdAt).valueOf()
                );

        }, [occurrences]);

    const [confirmDeleteUid, setConfirmDeleteUid] = useState(null);

    const confirmDeleteRef = useRef(null);

    useEffect(() => {

        if (!confirmDeleteUid) {
            return;
        }

        const handleClickOutside = event => {

            if (
                confirmDeleteRef.current &&
                !confirmDeleteRef.current.contains(
                    event.target
                )
            ) {
                setConfirmDeleteUid(null);
            }
        };

        document.addEventListener(
            'mousedown',
            handleClickOutside
        );

        return () => {
            document.removeEventListener(
                'mousedown',
                handleClickOutside
            );
        };

    }, [confirmDeleteUid]);

    if (!occurrencesOrdered.length) {
        return null;
    }

    return (
        <>
            <Divider sx={{ my: 2 }} />

            <Typography
                variant="subtitle2"
                color="primary"
                sx={{
                    fontWeight: 'bold',
                    mb: 1
                }}
            >
                Ocorrências registradas
            </Typography>

            <Box
                sx={{
                    maxHeight: {
                        xs: 140,
                        md: 160,
                        xl: 300
                    },
                    overflowY: 'auto',
                    pr: 1
                }}
            >
                {occurrencesOrdered.map(
                    occurrence => {

                        const motive =
                            MOTIVES.find(
                                item =>
                                    Number(item.id) ===
                                    Number(
                                        occurrence.motiveId
                                    )
                            );

                        const isCurrentUser =
                            Number(user.id) ===
                            Number(occurrence.userId);

                        const isConfirmingDelete =
                            confirmDeleteUid ===
                            occurrence.occurrenceUid;

                        return (
                            <Box
                                key={occurrence.occurrenceUid}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 1,
                                    py: 0.75,

                                    '&:not(:last-child)': {
                                        borderBottom: 1,
                                        borderColor: 'divider'
                                    }
                                }}
                            >
                                <AssignmentIcon
                                    color="primary"
                                    sx={{
                                        fontSize: 18,
                                        mt: 0.25
                                    }}
                                />

                                <Box
                                    sx={{
                                        minWidth: 0,
                                        flex: 1
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.75
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 'bold'
                                            }}
                                        >
                                            {motive?.label ??
                                                `Motivo ${occurrence.motiveId}`}
                                        </Typography>

                                        {isCurrentUser && (
                                            <Typography
                                                variant="caption"
                                                color="primary"
                                                sx={{
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                (Por você)
                                            </Typography>
                                        )}
                                    </Box>

                                    {occurrence.description && (
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{
                                                whiteSpace: 'pre-line',
                                                overflowWrap: 'anywhere'
                                            }}
                                        >
                                            {occurrence.description}
                                        </Typography>
                                    )}

                                    {occurrence.createdAt && (
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{
                                                display: 'block',
                                                mt: 0.25
                                            }}
                                        >
                                            {m(
                                                occurrence.createdAt
                                            ).format(
                                                'DD/MM/YYYY HH:mm'
                                            )}
                                        </Typography>
                                    )}
                                </Box>

                                {isCurrentUser && (
                                    <Box
                                        ref={
                                            isConfirmingDelete
                                                ? confirmDeleteRef
                                                : null
                                        }
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            flexShrink: 0
                                        }}
                                    >
                                        {isConfirmingDelete && (
                                            <Button
                                                size="small"
                                                color="warning"
                                                variant="contained"
                                                onClick={() => {
                                                    setConfirmDeleteUid(null);
                                                    onDelete?.(occurrence);
                                                }}
                                                sx={{
                                                    minWidth: 'auto',
                                                    px: 1,
                                                    py: 0.25,
                                                    fontSize: '0.7rem'
                                                }}
                                            >
                                                Confirmar?
                                            </Button>
                                        )}

                                        <Tooltip
                                            title={
                                                isConfirmingDelete
                                                    ? 'Cancelar exclusão'
                                                    : 'Excluir ocorrência'
                                            }
                                        >
                                            <IconButton
                                                size="small"
                                                color={isConfirmingDelete ? 'warning' : 'error'}
                                                onClick={() => {

                                                    if (isConfirmingDelete) {
                                                        setConfirmDeleteUid(null);
                                                        return;
                                                    }

                                                    setConfirmDeleteUid(
                                                        occurrence.occurrenceUid
                                                    );
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        position: 'relative',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}
                                                >
                                                    <DeleteOutlineIcon
                                                        fontSize="small"
                                                    />

                                                    {isConfirmingDelete && (
                                                        <Box
                                                            sx={{
                                                                position: 'absolute',
                                                                width: '120%',
                                                                height: '2px',
                                                                bgcolor: 'currentColor',
                                                                borderRadius: 1,
                                                                transform:
                                                                    'rotate(-45deg)'
                                                            }}
                                                        />
                                                    )}
                                                </Box>
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                )}
                            </Box>
                        );
                    }
                )}
            </Box>
        </>
    );
};

export default memo(TripOccurrences);